import React, { useEffect, useState } from "react";

import {
  Button,
  Col,
  Input,
  message,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
} from "antd";
import { arrayMoveImmutable } from "array-move";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";

import { MenuOutlined } from "@ant-design/icons";

import RefernEarnAddEditProductModal from "../../Components/RefernEarnAddEditProductModal";
import useDebounce from "../../hooks/useDebounce";
import { getRefernEarnProducts, reorderRefernEarnProduct } from "../api";

const formatIndianNumber = (value) => {
  return new Intl.NumberFormat("en-IN").format(value);
};
const DragHandle = SortableHandle(() => (
  <MenuOutlined style={{ cursor: "grab", color: "#999" }} />
));

const SortableItem = SortableElement((props) => <tr {...props} />);

const SortableBody = SortableContainer((props) => <tbody {...props} />);

const RefernEarnProducts = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    status: "", // listed | hidden | expired | deprecated
    limit: "", // onetime | subscription | onetime-subscription
  });
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [mode, setMode] = useState("add");
  const [product, setProduct] = useState(null);

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });
  const [tableKey, setTableKey] = useState(Date.now());

  const handleGetRefernEarnProducts = async () => {
    setLoading(true);
    try {
      const res = await getRefernEarnProducts({
        ...filters,
        search: debouncedSearch,
      });

      if (res?.code === 200) {
        setData(res.result.map((item, index) => ({ ...item, index })));
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleReorderRefernEarnProducts = async (newData) => {
    setLoading(true);
    try {
      const res = await reorderRefernEarnProduct({
        order: newData.map((item) => item._id),
      });

      if (res?.code === 200) {
        setData(newData);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "sort",
      width: 30,
      className: "drag-visible",
      render: () => <DragHandle />,
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "productName",
      ellipsis: true,
      width: 180,
    },
    {
      title: "Value",
      dataIndex: "totalProductPrice",
      key: "totalProductPrice",
      width: 100,
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.totalProductPrice - b.totalProductPrice,
      render: (value) =>
        value > 0 ? (
          <Statistic
            value={value}
            precision={0}
            formatter={formatIndianNumber}
            valueStyle={{ fontSize: 16 }}
            prefix={<span className="siteInr">&#8377;</span>}
          />
        ) : (
          <span>Free</span>
        ),
    },
    {
      title: "Rev. Share",
      dataIndex: "revenueShare",
      key: "revenueShare",
      width: 110,
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.revenueShare - b.revenueShare,
      render: (value) => (value != null ? `${value}%` : "-"),
    },
    {
      title: "Purchase/Signup Link",
      dataIndex: "productLink",
      key: "productLink",
      ellipsis: true,
      render: (value) => (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          style={{ color: "#1890ff" }}
        >
          {value}
        </a>
      ),
    },
    {
      title: "Exclusive",
      dataIndex: "isExclusive",
      key: "isExclusive",
      width: 100,
      filters: [
        {
          text: "Yes",
          value: true,
        },
        {
          text: "No",
          value: false,
        },
      ],
      onFilter: (value, record) => record.isExclusive === value,
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      filters: [
        {
          text: "Listed",
          value: "listed",
        },
        {
          text: "Hidden",
          value: "hidden",
        },
        {
          text: "Expired",
          value: "expired",
        },
      ],
      onFilter: (value, record) => record.status.indexOf(value) === 0,
      render: (value) => (
        <Tag
          color={
            // eslint-disable-next-line no-nested-ternary
            value === "listed" ? "green" : value === "hidden" ? "yellow" : "red"
          }
          style={{ textTransform: "capitalize" }}
        >
          {value}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            block
            onClick={() => {
              setMode("edit");
              setModalVisible(true);
              setProduct(record);
            }}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        data.slice(),
        oldIndex,
        newIndex
      ).filter((el) => !!el);
      handleReorderRefernEarnProducts(newData);
    }
  };

  const DraggableContainer = (props) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({ ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = data.findIndex((x) => x.index === restProps["data-row-key"]);
    return <SortableItem index={index} {...restProps} />;
  };

  useEffect(() => {
    handleGetRefernEarnProducts();
  }, [debouncedSearch, filters]);

  return (
    <>
      <Row gutter={6}>
        <Col span={12}>
          <Input
            minLength={2}
            className="searchbar"
            value={filters.search}
            style={{ width: "100%" }}
            placeholder="Search by partner name, email or mobile"
            onChange={(event) => setSearch(event.target.value)}
          />
        </Col>
        <Col span={12}>
          <Button
            type="primary"
            onClick={() => {
              setMode("add");
              setModalVisible(true);
              setProduct(null);
            }}
          >
            Add new Product
          </Button>
        </Col>
      </Row>
      <Row style={{ marginTop: "20px" }}>
        <Col span={12}>
          <Space>
            <Button
              type={filters.limit === "" ? "primary" : "default"}
              onClick={() => setFilters({ ...filters, limit: "" })}
            >
              All
            </Button>
            <Button
              type={filters.limit === "onetime" ? "primary" : "default"}
              onClick={() => setFilters({ ...filters, limit: "onetime" })}
            >
              One-time
            </Button>
            <Button
              type={filters.limit === "subscription" ? "primary" : "default"}
              onClick={() => setFilters({ ...filters, limit: "subscription" })}
            >
              Subscription
            </Button>
            <Button
              type={
                filters.limit === "onetime-subscription" ? "primary" : "default"
              }
              onClick={() =>
                setFilters({ ...filters, limit: "onetime-subscription" })
              }
            >
              One-time + Subscription
            </Button>
          </Space>
        </Col>
      </Row>

      <RefernEarnAddEditProductModal
        mode={mode}
        product={product}
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSave={() => {
          handleGetRefernEarnProducts();
          setModalVisible(false);
        }}
      />

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        size="middle"
        style={{
          marginTop: "20px",
        }}
        scroll={{ x: 900 }}
        pagination={false}
        rowKey="index"
        components={{
          body: {
            wrapper: DraggableContainer,
            row: DraggableBodyRow,
          },
        }}
      />
    </>
  );
};

export default RefernEarnProducts;
